/** @file Least-squares affine from image pixels to rigid-mesh vertices, used to place a mesh handle. */
import type { Affine } from '@pose2d/math/types';

/** Determinant of [[a, b, c], [d, e, f], [g, h, i]]. */
function det3(a: number, b: number, c: number, d: number, e: number, f: number, g: number, h: number, i: number): number {
  return a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
}

/**
 * Fits local = A * (u * origW, v * origH) over every vertex (normal equations, Cramer's rule). Exact when the
 * vertices are an affine image of the UVs (a mesh as created from its image); null when the UVs are collinear.
 */
export function fitPixelAffine(uvs: ArrayLike<number>, origW: number, origH: number, vertices: ArrayLike<number>): Affine | null {
  let sxx = 0;
  let sxy = 0;
  let sx = 0;
  let syy = 0;
  let sy = 0;
  let n = 0;
  let xa = 0;
  let xb = 0;
  let xc = 0;
  let ya = 0;
  let yb = 0;
  let yc = 0;
  for (let i = 0; i + 1 < uvs.length; i += 2) {
    const px = (uvs[i] ?? 0) * origW;
    const py = (uvs[i + 1] ?? 0) * origH;
    const lx = vertices[i] ?? 0;
    const ly = vertices[i + 1] ?? 0;
    sxx += px * px;
    sxy += px * py;
    sx += px;
    syy += py * py;
    sy += py;
    n += 1;
    xa += px * lx;
    xb += py * lx;
    xc += lx;
    ya += px * ly;
    yb += py * ly;
    yc += ly;
  }
  const det = det3(sxx, sxy, sx, sxy, syy, sy, sx, sy, n);
  if (!(Math.abs(det) > 1e-9 * Math.max(1, sxx * syy * n))) return null;
  const solve = (r0: number, r1: number, r2: number): [number, number, number] => [
    det3(r0, sxy, sx, r1, syy, sy, r2, sy, n) / det,
    det3(sxx, r0, sx, sxy, r1, sy, sx, r2, n) / det,
    det3(sxx, sxy, r0, sxy, syy, r1, sx, sy, r2) / det,
  ];
  const [a, b, tx] = solve(xa, xb, xc);
  const [c, d, ty] = solve(ya, yb, yc);
  return [a, b, c, d, tx, ty];
}
